<template>
	<div class="search-bar">
		<div class="search-bar__title">
			<div v-show="!hideTitle" style="flex: 1">
				<i class="el-icon-search" />
				{{ title }}
			</div>
			<el-button v-show="showMoreBtn" type="text" class="toggle-btn" @click="toggleSearch">
				{{ unfolding ? '收起' : '展开更多' }}
				<i :class="unfolding ? 'el-icon-arrow-up' : 'el-icon-arrow-down'"></i>
			</el-button>
		</div>

		<div
			ref="searchContentRef"
			class="search-bar__content search-trans"
			:style="{ height: searchContentMaxHeight }"
		>
			<!-- 旧版插槽方式 -->
			<div v-if="hasContentSlot">
				<slot name="content" />
			</div>

			<!-- 新版数据驱动方式 -->
			<el-form
				v-else
				ref="searchFormRef"
				:model="formModel"
				:rules="rules"
				:inline="inline"
				:label-width="labelWidth"
				:label-position="labelPosition"
				@submit.prevent="handleSearch"
			>
				<slot name="form-items-before" />

				<el-form-item
					v-for="(item, idx) in formItems"
					:key="idx"
					:label="item.label"
					:prop="item.prop"
					:label-width="item.labelWidth"
					:rules="item.rules"
				>
					<component
						:is="getComponentType(item.type)"
						v-model="formModel[item.prop]"
						v-bind="item.attrs || {}"
						v-on="item.listeners || {}"
					>
						<template v-if="Array.isArray(item.children) && item.children.length">
							<component
								v-for="(child, childIndex) in item.children"
								:key="childIndex"
								:is="getChildComponentType(child.type)"
								v-bind="child.attrs || {}"
							></component>
						</template>
					</component>
				</el-form-item>

				<slot name="form-items-after" />
			</el-form>
		</div>

		<div class="search-bar__bottom" :style="{ justifyContent: bottomPosition }">
			<el-button icon="el-icon-refresh" @click="handleReset">重置</el-button>
			<el-button v-show="hasSearchBtn" type="primary" icon="el-icon-search" @click="handleSearch">
				搜索
			</el-button>
		</div>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, useSlots, nextTick } from 'vue'

import SelectUser from '@/components/SelectUser'
const props = defineProps({
	// 表单配置项（新方式）
	formItems: {
		type: Array,
		default: () => []
	},
	// 表单数据模型（新方式）
	modelValue: {
		type: Object,
		default: () => ({})
	},
	// 表单项布局方式
	inline: {
		type: Boolean,
		default: true
	},
	// 标签宽度
	labelWidth: {
		type: String,
		default: 'auto'
	},
	// 标签位置
	labelPosition: {
		type: String,
		default: 'right',
		validator: val => ['left', 'right', 'top'].includes(val)
	},
	// 按钮组位置：'start' | 'center' | 'end'
	bottomPosition: {
		type: String,
		default: 'center'
	},
	// 是否隐藏标题
	hideTitle: {
		type: Boolean,
		default: false
	},
	// 自定义标题
	title: {
		type: String,
		default: '筛选搜索'
	},
	// 是否展示搜索按钮
	hasSearchBtn: {
		type: Boolean,
		default: true
	}
})

const emit = defineEmits(['update:modelValue', 'onReset', 'onSearch'])

const slots = useSlots()
const hasContentSlot = computed(() => !!slots.content)

const searchFormRef = ref(null)
const searchContentRef = ref(null)
const unfolding = ref(false)
const showMoreBtn = ref(false)
let resizeObserver = null

// 计算表单模型
const formModel = computed({
	get: () => props.modelValue,
	set: val => {
		emit('update:modelValue', val)
	}
})

// 计算表单规则
const rules = computed(() => {
	const rulesObj = {}
	props.formItems.forEach(item => {
		if (item.rules) {
			rulesObj[item.prop] = item.rules
		}
	})
	return rulesObj
})

// 计算搜索内容最大高度
const searchContentMaxHeight = computed(() => {
	return unfolding.value ? 'auto' : '48px'
})

// 获取组件类型
const getComponentType = (type = 'input') => {
	const componentMap = {
		input: 'el-input',
		select: 'el-select',
		date: 'el-date-picker',
		userSearch: SelectUser
		// 可以继续添加其他组件类型
	}
	return componentMap[type] || 'el-input'
}

// 获取子组件类型
const getChildComponentType = (type = 'option') => {
	const componentMap = {
		option: 'el-option',
		checkbox: 'el-checkbox',
		radio: 'el-radio'
		// 可以继续添加其他组件类型
	}
	return componentMap[type] || 'el-option'
}

// 切换搜索框展开/收起
const toggleSearch = () => {
	unfolding.value = !unfolding.value
}

// 处理搜索
const handleSearch = async () => {
	if (hasContentSlot.value) {
		// 旧版插槽方式，直接触发事件
		emit('onSearch')
	} else {
		// 新版数据驱动方式，先验证表单
		try {
			const valid = await searchFormRef.value?.validate()
			if (valid) {
				emit('onSearch', formModel.value)
			}
		} catch (error) {
			console.error('表单验证失败:', error)
		}
	}
}

// 处理重置
const handleReset = () => {
	if (hasContentSlot.value) {
		// 旧版插槽方式，直接触发事件
		emit('onReset')
	} else {
		// 新版数据驱动方式，重置表单
		searchFormRef.value?.resetFields()
		emit('onReset')
	}
}

// 更新显示更多按钮状态
const updateShowMoreBtn = () => {
	const searchBar = searchContentRef.value
	if (!searchBar) return

	// 获取实际内容容器（插槽模式下的第一个元素或是新版的 form 编辑器）
	const content = hasContentSlot.value
		? searchBar.querySelector('form') || searchBar.querySelector('div')
		: searchBar.querySelector('form')

	if (content) {
		// 这里的 60 是两行左右的高度阈值
		showMoreBtn.value = content.offsetHeight > 60
	}
}

onMounted(() => {
	updateShowMoreBtn()

	// 使用 ResizeObserver 监听内容区尺寸变化（包括切换 tab 导致的显示/隐藏）
	if (window.ResizeObserver && searchContentRef.value) {
		resizeObserver = new ResizeObserver(() => {
			updateShowMoreBtn()
		})
		resizeObserver.observe(searchContentRef.value)
	}

	// 监听窗口大小变化作为兜底
	window.addEventListener('resize', updateShowMoreBtn)
})

onUnmounted(() => {
	if (resizeObserver) {
		resizeObserver.disconnect()
		resizeObserver = null
	}
	window.removeEventListener('resize', updateShowMoreBtn)
})

// 监听表单项变化
watch(
	() => props.formItems,
	() => {
		nextTick(updateShowMoreBtn)
	},
	{ deep: true }
)
</script>

<style lang="scss" scoped>
.search-bar {
	margin-bottom: 16px;
	border: 1px solid #ebeef5;
	padding: 10px 16px;

	&__title {
		display: flex;
		justify-content: flex-end;
		align-items: center;
		padding-bottom: 10px;
		border-bottom: 1px solid #ebeef5;
		margin-bottom: 20px;
		line-height: 32px;
		color: #606266;
	}

	&__content {
		height: 48px;
		overflow: hidden;

		:deep(.el-form) {
			display: flex;
			flex-wrap: wrap;
			gap: 16px;

			.el-form-item {
				margin-bottom: 16px;
				margin-right: 0;
			}
		}
	}

	&__bottom {
		display: flex;
		margin-top: 16px;
	}
}

.search-trans {
	transition: height 0.3s ease;
}
</style>
